import { useMemo } from "react";
import { StackMeasurements, GetSolidVolumeFactorProps } from "@foresting-tomorrow/forest_tools_inventory";
import { woodStackCalculation, parseMeasurementNumber } from "./utils/calculations";
import { ChangeEvent, RefObject } from "react";

// material ui
import { Box, Divider, Grid } from "@mui/material";

// material ui icons

// project import
import RawWoodInputInformation from "./rawWoodInputInformation";
import RawWoodInputConversionFactor from "./rawWoodInputConversionFactor";
import RawWoodInputMeasurement from "./rawWoodInputMeasurement";
import RawWoodInputHandling from "./rawWoodInputHandling";
import RawWoodInputQuantities from "./rawWoodInputQuantities";
import RawWoodInputMeasurementLogs, {
  LogEntry,
  RawWoodInputMeasurementLogsHandler,
} from "./rawWoodInputMeasurementLogs";
import {
  BuyerStatusChangeProps,
  FirestoreInventoryItemProps,
} from "types/auth";
import { ActivityOption } from "components/estatesAndMapSelecter/getActivityOptions";
import { ContactType } from "components/shareData/shareDialog";
import {
  RawWoodInputInformationSpeciesHandle,
  RawWoodSpeciesItem,
} from "./rawWoodInputInformationSpecies";
import { RawWoodOptionProps } from "./options";
import TabPanel from "components/navigation/TabPanel";
import { InventoryDataConfigProps } from "types/appFunctionality";
import TextInformation from "components/display/textInformation";
import { useTranslation } from "utils/locales/utilityFunctions";

const handlingTabPanelStyle = {
  p: 0,
  overflowY: "auto",
  overflowX: "hidden",
};

const rawWoodContainerStyle = {
  mt: 0,
  minHeight: 260,
  width: "100%",
  minWidth: 0,
  overflowX: "hidden",
};

interface RawWoodInputProps {
  title: string;
  variant: FirestoreInventoryItemProps["inventoryType"];
  inventoryDataConfig: InventoryDataConfigProps;
  tabValue: number;
  countryCode: string;
  location: { lat: number; lng: number };
  inventoryId: string;
  uniqueInventoryId: number;
  inventoryDisplayReference: string;
  certification: string[];
  certificationNumbers: NonNullable<
    FirestoreInventoryItemProps["certificationNumbers"]
  >;
  certificationClaims: NonNullable<FirestoreInventoryItemProps["certificationClaims"]>;
  certificationNumber: string;
  originsOptions: ActivityOption[];
  origins?: ActivityOption[];
  responsibleOptions: ContactType[];
  responsible?: ContactType[];
  date: Date | null;
  initSpeciesArray?: RawWoodSpeciesItem[];
  speciesRef: RefObject<RawWoodInputInformationSpeciesHandle | null>;
  speciesOptions: any[];
  assortmentOptions: RawWoodOptionProps[];
  assortment: string;
  averageLogLength: string;
  averageLogDiameter: string;
  averageStackHeight: string;
  numericMeasurements: StackMeasurements | null;
  averageStackLength: string;
  woodChipManualVolume: string;
  woodChipManualCompFactor: string;
  woodLogAssortmentOptions: RawWoodOptionProps[];
  woodLogAssortment: string;
  isChipped: boolean;
  woodLogSpeciesOptions: any[];
  woodLogClassOptions: RawWoodOptionProps[];
  woodLogClassExportOptions: RawWoodOptionProps[];
  initLogRows: LogEntry[];
  logRowsRef: RefObject<RawWoodInputMeasurementLogsHandler | null>;
  measurements: {
    numberOfSections: number | "";
    numberOfSectionsBack: number | "";
    measureBothSides: boolean;
    stackLengthFront: string;
    stackLengthBack: string;
    frontHeights: string[];
    backHeights: string[];
    setNumberOfSections: (value: number | "") => void;
    setNumberOfSectionsBack: (value: number | "") => void;
    setMeasureBothSides: (value: boolean) => void;
    setStackLengthFront: (value: string) => void;
    setStackLengthBack: (value: string) => void;
    setFrontHeights: (heights: string[]) => void;
    setBackHeights: (heights: string[]) => void;
  };
  stacking: {
    stackingOptions: RawWoodOptionProps[];
    straightnessOptions: RawWoodOptionProps[];
    processingOptions: RawWoodOptionProps[];
    snowIceOptions: RawWoodOptionProps[];
    residuesOptions: RawWoodOptionProps[];
    rejectsOptions: RawWoodOptionProps[];
    battensOptions: RawWoodOptionProps[];
    stackingValue: string;
    straightnessValue: string;
    processingValue: string;
    snowIceValue: string;
    residuesValue: string;
    rejectsValue: string;
    battensValue: string;
    inputModeBattens: "count" | "volume";
    inputModeRejects: "count" | "volume";
    setStackingValue: (value: string) => void;
    setStraightnessValue: (value: string) => void;
    setProcessingValue: (value: string) => void;
    setSnowIceValue: (value: string) => void;
    setResiduesValue: (value: string) => void;
    handleSetBattensAndRejectsValue: (
      value: string,
      mode: "battens" | "rejects",
      inputMode: "count" | "volume"
    ) => void;
    setInputModeBattens: (mode: "count" | "volume") => void;
    setInputModeRejects: (mode: "count" | "volume") => void;
  };
  handling: {
    companyOptions: ContactType[];
    buyerOptions: ContactType[];
    buyerSelectionLocked: boolean;
    statusOptions: RawWoodOptionProps[];
    accessibilityOptions: RawWoodOptionProps[];
    buyerApprovalOptions: RawWoodOptionProps[];
    selectedBuyer: ContactType[];
    buyerApprovalRequirements: NonNullable<
      NonNullable<FirestoreInventoryItemProps["buyer"]>["approvalRequirements"]
    >;
    buyerApprovalRequirementsLoaded: boolean;
    releasedToBuyer: boolean;
    selectedLoggingCompanyString: string;
    selectedForwardingCompanyString: string;
    selectedChippingCompanyString: string;
    selectedLoggingCompany: ContactType[];
    selectedForwardingCompany: ContactType[];
    selectedChippingCompany: ContactType[];
    selectedStatus: string;
    selectedBuyerApprovalStatus: string;
    price: string;
    inventoryStatusNote: string;
    accessibility: string[];
    accessibilityNote: string;
    buyerNote: string;
    setSelectedLoggingCompanyString: (value: string) => void;
    setSelectedForwardingCompanyString: (value: string) => void;
    setSelectedChippingCompanyString: (value: string) => void;
    setSelectedBuyer: (value: ContactType[]) => void;
    setSelectedLoggingCompany: (value: ContactType[]) => void;
    setSelectedForwardingCompany: (value: ContactType[]) => void;
    setSelectedChippingCompany: (value: ContactType[]) => void;
    setSelectedStatus: (value: string) => void;
    setSelectedBuyerApprovalStatus: (
      value: BuyerStatusChangeProps["status"]
    ) => void;
    setPrice: (value: string) => void;
    setInventoryStatusNote: (value: string) => void;
    setAccessibility: (value: string[]) => void;
    setAccessibilityNote: (value: string) => void;
    setBuyerNote: (value: string) => void;
    setBuyerApprovalRequirements: (
      requirements: NonNullable<
        NonNullable<FirestoreInventoryItemProps["buyer"]>["approvalRequirements"]
      >,
    ) => void;
    setReleasedToBuyer: (value: boolean) => Promise<boolean>;
    setDeliveryNoteGenerating: (value: boolean) => void;
  };
  setOrigins: (v: any) => void;
  setResponsible: (v: any) => void;
  setCertification: (certification: string) => void;
  setCertificationNumbers: (
    certificationNumbers: NonNullable<
      FirestoreInventoryItemProps["certificationNumbers"]
    >
  ) => void;
  setCertificationClaims: (
    certificationClaims: NonNullable<FirestoreInventoryItemProps["certificationClaims"]>,
  ) => void;
  setDate: (date: Date | null) => void;
  setAssortment: (value: string) => void;
  setAverageLogLength: (event: ChangeEvent<HTMLInputElement>) => void;
  setAverageLogDiameter: (event: ChangeEvent<HTMLInputElement>) => void;
  setWoodChipManualVolume: (event: ChangeEvent<HTMLInputElement>) => void;
  setWoodChipManualCompFactor: (value: string) => void;
  setWoodLogAssortment: (value: string) => void;
  setIsChipped: (value: boolean) => void;
  saveFiles: (categories: string[], file: File[]) => Promise<void>;
}

const RawWoodInput = ({
  title,
  variant,
  inventoryDataConfig,
  tabValue,
  countryCode,
  location,
  inventoryId,
  uniqueInventoryId,
  inventoryDisplayReference,
  certification,
  certificationNumbers,
  certificationClaims,
  certificationNumber,
  originsOptions,
  origins,
  responsibleOptions,
  responsible,
  date,
  initSpeciesArray,
  speciesRef,
  speciesOptions,
  assortmentOptions,
  assortment,
  averageLogLength,
  averageLogDiameter,
  averageStackHeight,
  numericMeasurements,
  averageStackLength,
  woodChipManualVolume,
  woodChipManualCompFactor,
  woodLogAssortmentOptions,
  woodLogAssortment,
  isChipped,
  woodLogSpeciesOptions,
  woodLogClassOptions,
  woodLogClassExportOptions,
  initLogRows,
  logRowsRef,
  measurements,
  stacking,
  handling,
  setOrigins,
  setCertification,
  setCertificationNumbers,
  setCertificationClaims,
  setResponsible,
  setDate,
  setAssortment,
  setAverageLogLength,
  setAverageLogDiameter,
  setWoodChipManualVolume,
  setWoodChipManualCompFactor,
  setWoodLogAssortment,
  setIsChipped,
  saveFiles,
}: RawWoodInputProps) => {
  const { translate } = useTranslation();
  const woodStackResult = useMemo(() => variant === "WoodStack" && numericMeasurements ? woodStackCalculation({
    measurements: numericMeasurements,
    species: (initSpeciesArray ?? []).map(s => ({ species: s.id, mix: s.mix ? parseMeasurementNumber(s.mix) : 100 })),
    averageDiameterCm: parseMeasurementNumber(averageLogDiameter),
    logAverageLengthM: parseMeasurementNumber(averageLogLength),
    numberOfSupportLogs: stacking.inputModeBattens === "count" ? parseMeasurementNumber(stacking.battensValue || "0") : 0,
    numberOfCullLogs: stacking.inputModeRejects === "count" ? parseMeasurementNumber(stacking.rejectsValue || "0") : 0,
    volumeOfSupportLogsM3: stacking.inputModeBattens === "volume" ? parseMeasurementNumber(stacking.battensValue || "0") : 0,
    volumeOfCullLogsM3: stacking.inputModeRejects === "volume" ? parseMeasurementNumber(stacking.rejectsValue || "0") : 0,
    compensationFactors: {
      stackingMethod: stacking.stackingValue as GetSolidVolumeFactorProps["stackingMethod"],
      logStraightness: stacking.straightnessValue as GetSolidVolumeFactorProps["logStraightness"],
      processingResult: stacking.processingValue as GetSolidVolumeFactorProps["processingResult"],
      snowAndIceCoverage: stacking.snowIceValue as GetSolidVolumeFactorProps["snowAndIceCoverage"],
      loggingResidue: stacking.residuesValue as GetSolidVolumeFactorProps["loggingResidue"],
    },
  }) : null, [variant, numericMeasurements, initSpeciesArray, averageLogDiameter, averageLogLength, stacking.stackingValue, stacking.straightnessValue, stacking.processingValue, stacking.snowIceValue, stacking.residuesValue, stacking.battensValue, stacking.rejectsValue, stacking.inputModeBattens, stacking.inputModeRejects]);
  const disableInputs = inventoryDataConfig.changeData !== "enabled";
  const buyerApprovedDisable =
    handling.selectedBuyerApprovalStatus === "approved";

  const renderInventoryId = () => (
    <>
      <Grid item xs={12}>
        <Divider sx={{ mt: 1 }} />
      </Grid>

      <Grid item xs={12}>
        <TextInformation
          label={translate("inventory-id")}
          data={inventoryId}
          sx={{ mt: 0 }}
        />
      </Grid>
    </>
  );

  // --- Wood Stack --- //
  if (variant === "WoodStack") {
    return (
      <Box sx={rawWoodContainerStyle}>
        {/* Information */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={{ xs: 2 }} sx={{ mb: 2 }}>
            <RawWoodInputInformation
              variant={variant}
              showOriginSection={inventoryDataConfig.originSection !== "hidden"}
              disableInputs={disableInputs}
              buyerApprovedDisable={buyerApprovedDisable}
              countryCode={countryCode}
              certification={certification}
              certificationNumbers={certificationNumbers}
              certificationClaims={certificationClaims}
              legacyCertificationNumber={certificationNumber}
              originsOptions={originsOptions}
              origins={origins}
              responsibleOptions={responsibleOptions}
              responsible={responsible}
              date={date}
              initSpeciesArray={initSpeciesArray}
              speciesRef={speciesRef}
              speciesOptions={speciesOptions}
              assortmentOptions={assortmentOptions}
              assortment={assortment}
              averageLogLength={averageLogLength}
              averageLogDiameter={averageLogDiameter}
              woodLogAssortmentOptions={woodLogAssortmentOptions}
              woodLogAssortment={woodLogAssortment}
              setCertification={setCertification}
              setCertificationNumbers={setCertificationNumbers}
              setCertificationClaims={setCertificationClaims}
              setOrigins={setOrigins}
              setResponsible={setResponsible}
              setDate={setDate}
              setAssortment={setAssortment}
              setAverageLogLength={setAverageLogLength}
              setAverageLogDiameter={setAverageLogDiameter}
              setWoodLogAssortment={setWoodLogAssortment}
            />
            {renderInventoryId()}
          </Grid>
        </TabPanel>

        {/* Measurement */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={{ xs: 2 }} sx={{ mb: 2 }}>
            <RawWoodInputMeasurement
              variant={variant}
              disableInputs={disableInputs}
              buyerApprovedDisable={buyerApprovedDisable}
              {...measurements}
            />
          </Grid>
        </TabPanel>

        {/* Conversion factor */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={{ xs: 2 }} sx={{ mb: 2 }}>
            <RawWoodInputConversionFactor
              variant={variant}
              disableInputs={disableInputs}
              buyerApprovedDisable={buyerApprovedDisable}
              {...stacking}
              species={initSpeciesArray || []}
              averageDiameter={averageLogDiameter}
              averageLogLength={averageLogLength}
              averageStackHeight={averageStackHeight}
              woodStackResult={woodStackResult}
              averageStackLength={averageStackLength}
            />
          </Grid>
        </TabPanel>

        {/* Handling */}
        <TabPanel
          value={tabValue}
          index={3}
          withBox
          boxStyle={handlingTabPanelStyle}
        >
          <Grid container spacing={{ xs: 2 }} sx={{ mb: 2 }}>
            <RawWoodInputHandling
              title={title}
              inventoryDataConfig={inventoryDataConfig}
              variant={variant}
              disableInputs={disableInputs}
              buyerApprovedDisable={buyerApprovedDisable}
              {...handling}
              location={location}
              uniqueInventoryId={uniqueInventoryId}
              inventoryDisplayReference={inventoryDisplayReference}
              certification={certification}
              certificationNumbers={certificationNumbers}
              certificationClaims={certificationClaims}
              certificationNumber={certificationNumber}
              measurementData={{
                initSpeciesArray,
                averageLogLength,
                averageLogDiameter,
                averageStackHeight,
                numericMeasurements,
                woodStackResult,
                averageStackLength,
                responsible,
                origins,
                assortment,
                ...measurements,
                ...stacking,
              }}
              saveFiles={saveFiles}
            />
          </Grid>
        </TabPanel>
      </Box>
    );
  }
  // --- Wood Stack Chip --- //
  if (variant === "WoodChipStack") {
    return (
      <Box sx={rawWoodContainerStyle}>
        {/* Information */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={{ xs: 2 }} sx={{ mb: 2 }}>
            <RawWoodInputInformation
              variant={variant}
              showOriginSection={inventoryDataConfig.originSection !== "hidden"}
              disableInputs={disableInputs}
              buyerApprovedDisable={buyerApprovedDisable}
              countryCode={countryCode}
              certification={certification}
              certificationNumbers={certificationNumbers}
              certificationClaims={certificationClaims}
              legacyCertificationNumber={certificationNumber}
              originsOptions={originsOptions}
              origins={origins}
              responsibleOptions={responsibleOptions}
              responsible={responsible}
              date={date}
              initSpeciesArray={initSpeciesArray}
              speciesRef={speciesRef}
              speciesOptions={speciesOptions}
              assortmentOptions={assortmentOptions}
              assortment={assortment}
              averageLogLength={averageLogLength}
              averageLogDiameter={averageLogDiameter}
              woodLogAssortmentOptions={woodLogAssortmentOptions}
              woodLogAssortment={woodLogAssortment}
              isChipped={isChipped}
              setCertification={setCertification}
              setCertificationNumbers={setCertificationNumbers}
              setCertificationClaims={setCertificationClaims}
              setOrigins={setOrigins}
              setResponsible={setResponsible}
              setDate={setDate}
              setAssortment={setAssortment}
              setAverageLogLength={setAverageLogLength}
              setAverageLogDiameter={setAverageLogDiameter}
              setWoodLogAssortment={setWoodLogAssortment}
              setIsChipped={setIsChipped}
            />
            {renderInventoryId()}
          </Grid>
        </TabPanel>

        {/* Quantities */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={{ xs: 2 }} sx={{ mb: 2 }}>
            <RawWoodInputQuantities
              variant={variant}
              disableInputs={disableInputs}
              buyerApprovedDisable={buyerApprovedDisable}
              isChipped={isChipped}
              manualVolume={woodChipManualVolume}
              stackLength={measurements.stackLengthFront}
              averageLogLength={averageLogLength}
              averageStackHeight={averageStackHeight}
              numericMeasurements={numericMeasurements}
              numberOfSections={measurements.numberOfSections}
              heights={measurements.frontHeights}
              manualCompFactor={woodChipManualCompFactor}
              species={initSpeciesArray}
              setStackLength={measurements.setStackLengthFront}
              setNumberOfSections={measurements.setNumberOfSections}
              setHeights={measurements.setFrontHeights}
              setManualVolume={setWoodChipManualVolume}
              setManualCompFactor={setWoodChipManualCompFactor}
            />
          </Grid>
        </TabPanel>

        {/* Handling */}
        <TabPanel
          value={tabValue}
          index={2}
          withBox
          boxStyle={handlingTabPanelStyle}
        >
          <Grid container spacing={{ xs: 2 }} sx={{ mb: 2 }}>
            <RawWoodInputHandling
              title={title}
              variant={variant}
              inventoryDataConfig={inventoryDataConfig}
              disableInputs={disableInputs}
              buyerApprovedDisable={buyerApprovedDisable}
              {...handling}
              location={location}
              uniqueInventoryId={uniqueInventoryId}
              inventoryDisplayReference={inventoryDisplayReference}
              certification={certification}
              certificationNumbers={certificationNumbers}
              certificationClaims={certificationClaims}
              certificationNumber={certificationNumber}
              measurementData={{
                initSpeciesArray,
                averageLogLength,
                averageLogDiameter,
                averageStackHeight,
                numericMeasurements,
                averageStackLength,
                responsible,
                origins,
                ...measurements,
                woodChipManualVolume,
                woodChipManualCompFactor,
              }}
              saveFiles={saveFiles}
            />
          </Grid>
        </TabPanel>
      </Box>
    );
  }
  // --- Wood Log --- //
  if (variant === "WoodLogStack") {
    return (
      <Box sx={rawWoodContainerStyle}>
        {/* Information */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={{ xs: 2 }} sx={{ mb: 2 }}>
            <RawWoodInputInformation
              countryCode={countryCode}
              variant={variant}
              showOriginSection={inventoryDataConfig.originSection !== "hidden"}
              disableInputs={disableInputs}
              buyerApprovedDisable={buyerApprovedDisable}
              certification={certification}
              certificationNumbers={certificationNumbers}
              certificationClaims={certificationClaims}
              legacyCertificationNumber={certificationNumber}
              originsOptions={originsOptions}
              origins={origins}
              responsibleOptions={responsibleOptions}
              responsible={responsible}
              date={date}
              initSpeciesArray={initSpeciesArray}
              speciesRef={speciesRef}
              speciesOptions={speciesOptions}
              assortmentOptions={assortmentOptions}
              assortment={assortment}
              averageLogLength={averageLogLength}
              averageLogDiameter={averageLogDiameter}
              woodLogAssortmentOptions={woodLogAssortmentOptions}
              woodLogAssortment={woodLogAssortment}
              setCertification={setCertification}
              setCertificationNumbers={setCertificationNumbers}
              setCertificationClaims={setCertificationClaims}
              setOrigins={setOrigins}
              setResponsible={setResponsible}
              setDate={setDate}
              setAssortment={setAssortment}
              setAverageLogLength={setAverageLogLength}
              setAverageLogDiameter={setAverageLogDiameter}
              setWoodLogAssortment={setWoodLogAssortment}
            />
            {renderInventoryId()}
          </Grid>
        </TabPanel>

        {/* Measurement */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={{ xs: 2 }} sx={{ mb: 2 }}>
            <RawWoodInputMeasurementLogs
              variant={variant}
              disableInputs={disableInputs}
              buyerApprovedDisable={buyerApprovedDisable}
              assortment={woodLogAssortment}
              logClassOptions={woodLogClassOptions}
              logClassExportOptions={woodLogClassExportOptions}
              speciesOptions={woodLogSpeciesOptions}
              initRows={initLogRows}
              ref={logRowsRef}
              doubleDiameter={false}
              woodLogAssortment={woodLogAssortment}
            />
          </Grid>
        </TabPanel>

        {/* Handling */}
        <TabPanel
          value={tabValue}
          index={2}
          withBox
          boxStyle={handlingTabPanelStyle}
        >
          <Grid container spacing={{ xs: 2 }} sx={{ mb: 2 }}>
            <RawWoodInputHandling
              title={title}
              variant={variant}
              inventoryDataConfig={inventoryDataConfig}
              disableInputs={disableInputs}
              buyerApprovedDisable={buyerApprovedDisable}
              uniqueInventoryId={uniqueInventoryId}
              inventoryDisplayReference={inventoryDisplayReference}
              certification={certification}
              certificationNumbers={certificationNumbers}
              certificationClaims={certificationClaims}
              certificationNumber={certificationNumber}
              {...handling}
              location={location}
              measurementData={{
                initSpeciesArray,
                averageLogLength,
                averageLogDiameter,
                averageStackHeight,
                numericMeasurements,
                averageStackLength,
                responsible,
                origins,
                woodLogAssortment,
                initLogRows,
                ...measurements,
              }}
              saveFiles={saveFiles}
            />
          </Grid>
        </TabPanel>
      </Box>
    );
  }
  // --- / --- //
  return null;
};

export default RawWoodInput;
